import { OutlinedTextFieldProps } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FormField } from 'src/components';
import { EllipsesMenuOption } from 'src/components/EllipsesMenu/ellipses-menu';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  RecordFolder,
  RecordFolderArea,
  useCurrentUserProfile,
  useGroupRules,
  UserGroupRules,
  VIEW_ASSIGNMENT_ENTITIES,
} from 'src/graphql';
import {
  RecordFolderVisibility,
  useCreateRecordFolder,
  useDeleteRecordFolder,
  useRecordFolderVisibility,
  useUpdateRecordFolder,
} from 'src/graphql/entities/record-folder';
import { useCreateRecordFolderAssignment } from 'src/graphql/entities/record-folder-assignment';

import { useGlobalStore } from 'src/store';

export interface CreateRecordFolderFormValues {
  name: string;
}

// encapsulates some navigation specific logic in regards of folders management
// such as folder actions, edit name form values etc.
export const useCreateFolderOptions = () => {
  const submitButtonRef = useRef<HTMLButtonElement | null>(null);
  const [editingFolderId, setEditingFolderId] = useState<RecordFolder['id'] | null>(null);

  const [folderCreationInProgressType, setFolderCreationInProgressType] = useState<
    // no need to an enum since it's fully internal hook logic that uses those strings
    // for a simple comparison like const isPrivate type === 'private' ? true : false,
    // it is not going to be shared anywhere
    'private' | 'public' | ''
  >('');

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId() ?? PLATFORM_PARTNER_ID;
  const { user } = useCurrentUserProfile();
  const { createRecordFolder } = useCreateRecordFolder();
  const { deleteRecordFolder } = useDeleteRecordFolder();
  const { updateRecordFolder } = useUpdateRecordFolder();
  const { createRecordFolderAssignment } = useCreateRecordFolderAssignment({
    entityFilter: VIEW_ASSIGNMENT_ENTITIES,
  });
  const { showRecordFolder, hideRecordFolder } = useRecordFolderVisibility();
  const { checkValidity } = useGroupRules();

  const getCreateFolderOptions = useCallback(
    (recordFolder?: RecordFolder) => {
      const options: EllipsesMenuOption[] = [];

      if (recordFolder) {
        const isHidden = recordFolder.visibility === RecordFolderVisibility.Hidden;
        options.push({
          icon: isHidden ? 'eye' : 'eye-off',
          iconLibrary: 'lucide',
          label: isHidden ? 'Remove from hidden' : 'Move to hidden',
          onClick: () => {
            if (isHidden) {
              showRecordFolder(recordFolder);
            } else {
              hideRecordFolder(recordFolder);
            }
          },
        });
      }

      if (checkValidity([UserGroupRules.AccessToManagePublicFolders])) {
        options.push({
          label: 'Add public folder',
          icon: 'globe',
          iconLibrary: 'lucide',
          onClick: () => {
            setFolderCreationInProgressType('public');
          },
        });
      }

      options.push({
        icon: 'lock',
        iconLibrary: 'lucide',
        label: 'Add personal folder',
        onClick: () => {
          setFolderCreationInProgressType('private');
        },
      });

      if (
        recordFolder &&
        recordFolder?.partnerId === selectedPartnerId &&
        (checkValidity([UserGroupRules.AccessToManagePublicFolders]) ||
          (recordFolder.userId === user?.userId && recordFolder.isPrivate))
      ) {
        options.push(
          {
            label: 'Rename',
            icon: 'pencil',
            iconLibrary: 'lucide',
            onClick: () => {
              setEditingFolderId(null);
              setTimeout(() => setEditingFolderId(recordFolder.id));
            },
          },
          {
            label: 'Delete',
            icon: 'trash-2',
            iconLibrary: 'lucide',
            danger: true,
            onClick: () => {
              void deleteRecordFolder(recordFolder);
            },
          },
        );
      }

      return options;
    },
    [
      selectedPartnerId,
      user,
      showRecordFolder,
      hideRecordFolder,
      deleteRecordFolder,
      checkValidity,
    ],
  );

  const handleCreateFolderSubmit = useCallback(
    ({ name }: { name: string }) => {
      void createRecordFolder({
        name,
        isPrivate: folderCreationInProgressType === 'private',
        icon: 'folder',
        area: RecordFolderArea.View,
        partnerId: selectedPartnerId,
      });

      setFolderCreationInProgressType('');
    },

    [createRecordFolder, folderCreationInProgressType, selectedPartnerId],
  );

  const triggerSubmit = useCallback(() => {
    submitButtonRef?.current?.click();
  }, []);

  const createFolderFormFields = useMemo<FormField[]>(() => {
    const commonProps: {
      textFieldProps: OutlinedTextFieldProps;
    } = {
      textFieldProps: {
        variant: 'outlined' as const,
        size: 'small',
        sx: {
          marginTop: (theme) => theme.spacing(1),
        },
      },
    };

    return [
      {
        type: 'textfield' as const,
        name: 'name' as const,
        label: '',
        defaultValue: '',
        handleBlur: () => {
          setFolderCreationInProgressType('');
        },
        textFieldProps: commonProps.textFieldProps,
      },
    ];
  }, []);

  const handleEditFolder = useCallback(
    (name: string, recordFolder: RecordFolder) => {
      updateRecordFolder(recordFolder, { name });
      setEditingFolderId(null);
    },
    [updateRecordFolder],
  );

  return {
    createFolderFormFields,
    hasStartedCreation: !!folderCreationInProgressType,
    onSubmit: handleCreateFolderSubmit,
    submitButtonRef,
    editingFolderId,
    triggerSubmit,
    getCreateFolderOptions,
    handleEditFolder,
    createRecordFolderAssignment,
  };
};
